import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { resolveUrl } from "@/components/VisionSlideshow";
import { toast } from "sonner";
import { ImagePlus, Trash2, Eye, X, Sparkles, ChevronLeft, ChevronRight, Star } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const CATEGORIES = ["Health & Body", "Dream Home", "Travel & Experiences", "Dream Garage", "Freedom", "Family", "Other"];

export const VisionBoard = ({ images, reload }) => {
  const fileRef = useRef(null);
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [touchX, setTouchX] = useState(null);
  const [mode, setMode] = useState("upload");
  const [aiPrompt, setAiPrompt] = useState("");
  const [generating, setGenerating] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be under 8MB");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("category", category);
      form.append("caption", caption);
      await api.post("/vision/images", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Vision added to your board");
      setCaption("");
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const generate = async () => {
    if (!aiPrompt.trim()) {
      toast.error("Describe your vision first");
      return;
    }
    setGenerating(true);
    try {
      await api.post("/vision/generate", { prompt: aiPrompt, category, caption });
      toast.success("Vision generated and added to your board");
      setAiPrompt("");
      setCaption("");
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/vision/images/${id}`);
      toast.success("Removed from vision board");
      reload();
    } catch { toast.error("Couldn't remove image"); }
  };

  const reorder = async (from, to) => {
    if (to < 0 || to >= images.length || from === to) return;
    const ids = images.map((i) => i.id);
    const [moved] = ids.splice(from, 1);
    ids.splice(to, 0, moved);
    try {
      await api.patch("/vision/reorder", { ids });
      reload();
    } catch { toast.error("Couldn't reorder"); }
  };

  const selectedIdx = selected ? images.findIndex((img) => img.id === selected.id) : -1;

  const navigate = (dir) => {
    if (!images.length || selectedIdx === -1) return;
    setSelected(images[(selectedIdx + dir + images.length) % images.length]);
  };

  useEffect(() => {
    if (!selected) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") navigate(-1);
      if (e.key === "ArrowRight") navigate(1);
      if (e.key === "Escape") setSelected(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  return (
    <section className="max-w-7xl mx-auto px-6 pb-24" data-testid="vision-board">
      <div className="border border-white/10 rounded-md bg-[#0F0F11] p-8">
        <div className="flex items-center gap-3 mb-2">
          <Eye className="w-5 h-5 text-[#D4AF37]" />
          <h3 className="font-display text-2xl text-white" data-testid="vision-board-heading">The Vision Board</h3>
        </div>
        <p className="text-xs text-zinc-500 mb-8">What it all points to. These images rotate behind your empire every 5 seconds — upload the life you're designing. The first image leads the slideshow — use the arrows or star to put your favourites up front.</p>

        <div className="flex gap-2 mb-4" data-testid="vision-mode-toggle">
          <button
            type="button" onClick={() => setMode("upload")}
            className={`text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-2 rounded-sm border transition-colors duration-200 ${mode === "upload" ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "text-zinc-400 border-white/10 hover:border-white/30"}`}
            data-testid="vision-mode-upload"
          >
            Upload
          </button>
          <button
            type="button" onClick={() => setMode("ai")}
            className={`inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-2 rounded-sm border transition-colors duration-200 ${mode === "ai" ? "bg-[#00E5FF] text-black border-[#00E5FF]" : "text-zinc-400 border-white/10 hover:border-white/30"}`}
            data-testid="vision-mode-ai"
          >
            <Sparkles className="w-3.5 h-3.5" /> AI Generate
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-8 sm:flex-wrap">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-sm sm:w-56" data-testid="vision-category-select">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent className="bg-[#17171A] border-white/10 text-white">
              {CATEGORIES.map((c) => (
                <SelectItem key={c} value={c} data-testid={`vision-category-${c.toLowerCase().replace(/[^a-z]/g, "-")}`}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {mode === "ai" ? (
            <>
              <Input
                value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="Describe the vision… e.g. matte black G-Wagon outside a modern mansion at night"
                className="bg-white/5 border-white/10 text-white rounded-sm flex-1 sm:min-w-72 focus-visible:ring-[#00E5FF]" data-testid="vision-prompt-input"
              />
              <button
                type="button" disabled={generating} onClick={generate}
                className="inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-[#00E5FF] text-black text-xs font-bold uppercase tracking-[0.2em] rounded-sm hover:bg-[#4dedff] transition-colors duration-200 disabled:opacity-50"
                data-testid="vision-generate-button"
              >
                <Sparkles className="w-4 h-4" /> {generating ? "Dreaming…" : "Generate"}
              </button>
            </>
          ) : (
            <>
              <Input
                value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Caption (optional) — e.g. Byron Bay house, 2028"
                className="bg-white/5 border-white/10 text-white rounded-sm flex-1 focus-visible:ring-[#D4AF37]" data-testid="vision-caption-input"
              />
              <button
                type="button" disabled={uploading} onClick={() => fileRef.current?.click()}
                className="inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-[0.2em] rounded-sm hover:bg-[#e5c555] transition-colors duration-200 disabled:opacity-50"
                data-testid="vision-upload-button"
              >
                <ImagePlus className="w-4 h-4" /> {uploading ? "Uploading…" : "Add Vision"}
              </button>
            </>
          )}
          <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" data-testid="vision-file-input" />
        </div>
        {mode === "ai" && (
          <p className="text-[10px] text-zinc-600 -mt-5 mb-6 uppercase tracking-widest" data-testid="vision-ai-note">
            Powered by GPT Image 1 · generation takes up to a minute · caption optional below
          </p>
        )}

        {images.length === 0 ? (
          <p className="text-sm text-zinc-500" data-testid="vision-empty">No visions yet. Upload the first image of the life you're building.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {images.map((img, i) => (
              <motion.div
                key={img.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i }}
                className="group relative aspect-[4/3] rounded-sm overflow-hidden border border-white/10 cursor-pointer hover:border-[#D4AF37]/50 transition-colors duration-300"
                data-testid={`vision-image-${img.id}`}
                onClick={() => setSelected(img)}
              >
                <img src={resolveUrl(img)} alt={img.caption || img.category} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <p className="text-[9px] uppercase tracking-[0.25em] text-[#D4AF37]">{img.category}</p>
                  {img.caption && <p className="text-xs text-white/80 mt-0.5 truncate">{img.caption}</p>}
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); remove(img.id); }}
                  className="absolute top-2 right-2 p-1.5 bg-black/60 backdrop-blur-md border border-white/10 rounded-sm text-zinc-400 hover:text-red-400 transition-colors duration-200 sm:opacity-0 sm:group-hover:opacity-100"
                  data-testid={`vision-delete-${img.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
                {i === 0 ? (
                  <span
                    className="absolute top-2 left-2 inline-flex items-center gap-1 text-[8px] font-bold uppercase tracking-[0.2em] px-2 py-1 bg-[#D4AF37] text-black rounded-sm"
                    data-testid="vision-lead-badge"
                  >
                    <Star className="w-2.5 h-2.5 fill-black" /> Leads
                  </span>
                ) : (
                  <button
                    onClick={(e) => { e.stopPropagation(); reorder(i, 0); }}
                    className="absolute top-2 left-2 p-1.5 bg-black/60 backdrop-blur-md border border-white/10 rounded-sm text-zinc-400 hover:text-[#D4AF37] transition-colors duration-200 sm:opacity-0 sm:group-hover:opacity-100"
                    title="Make this the lead image"
                    data-testid={`vision-make-lead-${img.id}`}
                  >
                    <Star className="w-3.5 h-3.5" />
                  </button>
                )}
                <div className="absolute bottom-2 right-2 flex gap-1 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200">
                  <button
                    onClick={(e) => { e.stopPropagation(); reorder(i, i - 1); }}
                    disabled={i === 0}
                    className="p-1.5 bg-black/60 backdrop-blur-md border border-white/10 rounded-sm text-zinc-400 hover:text-[#D4AF37] transition-colors duration-200 disabled:opacity-30"
                    data-testid={`vision-move-left-${img.id}`}
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); reorder(i, i + 1); }}
                    disabled={i === images.length - 1}
                    className="p-1.5 bg-black/60 backdrop-blur-md border border-white/10 rounded-sm text-zinc-400 hover:text-[#D4AF37] transition-colors duration-200 disabled:opacity-30"
                    data-testid={`vision-move-right-${img.id}`}
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setSelected(null)}
            className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-md flex items-center justify-center p-6"
            data-testid="vision-lightbox"
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="relative max-w-5xl w-full"
              onClick={(e) => e.stopPropagation()}
              onTouchStart={(e) => setTouchX(e.touches[0].clientX)}
              onTouchEnd={(e) => {
                if (touchX === null) return;
                const dx = e.changedTouches[0].clientX - touchX;
                if (dx > 50) navigate(-1);
                else if (dx < -50) navigate(1);
                setTouchX(null);
              }}
            >
              <img src={resolveUrl(selected)} alt={selected.caption || selected.category} className="w-full max-h-[80vh] object-contain rounded-sm border border-white/10" />
              {images.length > 1 && (
                <>
                  <button
                    onClick={() => navigate(-1)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 p-2.5 bg-black/60 backdrop-blur-md border border-white/10 rounded-full text-zinc-300 hover:text-[#D4AF37] hover:border-[#D4AF37]/50 transition-colors duration-200"
                    data-testid="lightbox-prev" aria-label="Previous image"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => navigate(1)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2.5 bg-black/60 backdrop-blur-md border border-white/10 rounded-full text-zinc-300 hover:text-[#D4AF37] hover:border-[#D4AF37]/50 transition-colors duration-200"
                    data-testid="lightbox-next" aria-label="Next image"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
              <div className="flex items-center justify-between mt-3 gap-4">
                <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">
                  {selected.category}
                  {selected.caption && <span className="text-zinc-400 normal-case tracking-normal"> — {selected.caption}</span>}
                </p>
                <div className="flex items-center gap-3 shrink-0">
                  {images.length > 1 && (
                    <span className="text-[10px] tracking-[0.25em] text-zinc-500" data-testid="lightbox-counter">
                      {selectedIdx + 1} / {images.length}
                    </span>
                  )}
                  <button
                    onClick={() => setSelected(null)}
                    className="p-2 border border-white/10 rounded-sm text-zinc-400 hover:text-white hover:border-white/30 transition-colors duration-200"
                    data-testid="vision-lightbox-close"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
